import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Col,
  Divider,
  FlexboxGrid,
  Form,
  FormInstance,
  Grid,
  Row,
  Stack,
  Text,
} from 'rsuite';
import FileDownloadIcon from '@rsuite/icons/FileDownload';
import {
  selectCalculatedTaxData,
  selectTaxFlowData,
} from '../../store/slices/tax/taxSelectors';
import dayjs from 'dayjs';
import { resetCalculateTax } from '../../store/slices/tax/taxSlice';
import {
  deriveConfigExpressTaxCertifications,
  selectDownloadSummary,
} from '../../store/slices/config/configSelectors';
import AnnotatedCheckbox from '../../components/form/AnnotatedCheckbox';
import {
  constructMarkup,
  convertNumberToCurrency,
  getAddressWithUnitNumber,
} from '../../common/utils';
import FormTextField from '../../components/form/FormTextField';
import { checkedValidation, requiredValidation } from '../../common/validation';
import { FLOWS_ENUM } from '../../common/enums';
import { setSignatoryName } from '../../store/slices/payment/paymentSlice';
import { payNothingTax } from '../../store/slices/payment/paymentThunk';
import {
  selectCurrentUserEmail,
  selectCurrentIDToken,
} from '../../store/slices/auth/authSelectors';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import Notice from '../../components/user-feedback/Notice';
import IconIndicator from '../../components/user-feedback/IconIndicator';
import { TaxFilters } from '../../components/tax/TaxFilters/TaxFilters';
import { TaxConfirmPeriodSection } from '../../components/tax/TaxConfirmPeriodSection';
import { downloadTaxSummary } from '../../api/payment';
import { downloadBlob } from '../../common/spreadsheetUtils';
import FlowLayout from 'src/components/layout/FlowLayout';
import { useAppDispatch } from 'src/store/store';
import {
  CalculatedLicense,
  CalculatedPeriodLicense,
  CalculatedTotDetail,
  PaymentPeriod,
  TaxableActivity,
} from 'src/types/tax';
import { CheckType } from 'schema-typed';
import {
  selectPaymentCheckFailed,
  selectPaymentSuccessInfo,
} from 'src/store/slices/payment/paymentSelectors';
import { License } from 'src/types/licenses';
import { goToNextStep } from 'src/store/slices/flow/flowSlice';
import { selectNextStepPath } from 'src/store/slices/flow/flowSelectors';
export interface TaxConfirmFormValues {
  signatory: string;
  date: string;
  [key: string]: string | boolean | undefined;
}

const DATE_FORMAT = 'YYYY-MM-DD';

const TaxConfirmAndSign = () => {
  const dispatch = useAppDispatch();
  const placeNavigate = usePlaceNavigate();
  const form = useRef<FormInstance<TaxConfirmFormValues>>(null);

  const token = useSelector(selectCurrentIDToken);

  const formData = useSelector(selectTaxFlowData);
  const certifications: string[] = useSelector(
    deriveConfigExpressTaxCertifications
  );
  const calculatedTaxData = useSelector(selectCalculatedTaxData);
  const selectedEmailAddress: string = useSelector(selectCurrentUserEmail);
  const downloadSummary = useSelector(selectDownloadSummary);
  const paymentSuccess = useSelector(selectPaymentSuccessInfo);
  const paymentCheckFailed = useSelector(selectPaymentCheckFailed);
  const selectedNextStepPath = useSelector(selectNextStepPath);

  const [processingPayNothing, setProcessingPayNothing] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<[string, string]>([
    '',
    '',
  ]);

  const [formError, setFormError] = useState<Record<string, boolean>>({});
  const [formValues, setFormValues] = useState<TaxConfirmFormValues>({
    signatory: '',
    date: '',
  });

  useEffect(() => {
    // When payment has completed successfully just redirect to the success page
    if (paymentSuccess && !paymentCheckFailed) {
      setProcessingPayNothing(false);
      if (paymentSuccess.paymentConfirmation) {
        placeNavigate('/tax-success');
      }
    }
  }, [paymentSuccess, paymentCheckFailed, placeNavigate]);

  useEffect(() => {
    // Reset the calculate tax properties in redux
    // We need to do this so that if we navigate back to the previous page we are not immidiatley sent back to this page
    dispatch(resetCalculateTax());
  }, [dispatch]);

  const selectedPeriods: PaymentPeriod[] = useMemo(() => {
    return formData.selectedLicenses || []; //todo if there are no licenses what should happen on this page
  }, [formData]);

  const datesAreTheSame = useCallback((dateOne: string, dateTwo: string) => {
    const dayOneDayJs = dayjs(dateOne);
    const dayTwoDayJs = dayjs(dateTwo);
    return dayOneDayJs.isSame(dayTwoDayJs);
  }, []);

  const formModel = useMemo((): TaxConfirmFormValues => {
    const certificationMap: Record<string, boolean> = {};
    certifications?.forEach((_c, idx) => {
      certificationMap[`certification_${idx}`] = false;
    });

    return {
      ...certificationMap,
      signatory: '',
      date: dayjs().format(DATE_FORMAT),
    };
  }, [certifications]);

  const validationModel = useMemo<
    Record<string, typeof requiredValidation | typeof checkedValidation>
  >(() => {
    const certificationValidationMap: Record<string, typeof checkedValidation> =
      {};
    if (certifications) {
      certifications.forEach(
        (_c, idx) =>
          (certificationValidationMap[`certification_${idx}`] =
            checkedValidation)
      );
    }
    return {
      signatory: requiredValidation,
      ...certificationValidationMap,
    };
  }, [certifications]);

  useEffect(() => {
    setFormValues(formModel);
  }, [formModel, setFormValues]);

  const getAddress = useMemo(() => {
    return (licenseNumber: string, startDate: string, endDate: string) => {
      const matchingPeriod = selectedPeriods.find(
        (period: PaymentPeriod) =>
          datesAreTheSame(period.startDate, startDate) &&
          datesAreTheSame(period.endDate, endDate)
      );

      const matchingLicense = matchingPeriod?.licenses.find(
        (license: License) => license.license === licenseNumber
      );
      const address = matchingLicense?.address;

      if (address) {
        return getAddressWithUnitNumber(address, matchingLicense?.unitNumber);
      }
      return 'Unkown';
    };
  }, [selectedPeriods, datesAreTheSame]);

  const findOrCreateGroupForPeriod = (
    groups: CalculatedLicense[],
    item: CalculatedTotDetail
  ): CalculatedLicense => {
    const formattedStartDate = dayjs(item.startDate).format(DATE_FORMAT);
    const formattedEndDate = dayjs(item.endDate).format(DATE_FORMAT);

    let group = groups?.find(
      (group: CalculatedLicense) =>
        datesAreTheSame(group.startDate, item.startDate) &&
        datesAreTheSame(group.endDate, item.endDate)
    );

    if (!group) {
      group = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        label: item.period,
        licenses: [],
        totalDueForPeriod: 0,
        providers: [],
        licenseNumber: '',
      };
      groups.push(group);
    }
    return group;
  };

  const convertedDataGroupedByPeriod: CalculatedLicense[] = useMemo(() => {
    if (!calculatedTaxData?.details) return [];

    const sortedData: Array<CalculatedTotDetail> = [
      ...(calculatedTaxData?.details ?? []),
    ]?.sort((a, b) => {
      const bDate = dayjs(b.endDate);
      const aDate = dayjs(a.endDate);
      return bDate.diff(aDate) >= 0 ? 1 : -1;
    });

    // Group data by start and end dates
    const displayTaxablePeriods = sortedData?.reduce(
      (
        allTaxablePeriods: Array<CalculatedLicense>,
        calculatedLicenseTaxData: CalculatedTotDetail
      ): CalculatedLicense[] => {
        const taxablePeriod = findOrCreateGroupForPeriod(
          allTaxablePeriods,
          calculatedLicenseTaxData
        );

        const license: CalculatedPeriodLicense = {
          licenseNumber: calculatedLicenseTaxData.licence_no,
          address: getAddress(
            calculatedLicenseTaxData.licence_no,
            taxablePeriod.startDate,
            taxablePeriod.endDate
          ),
          providers: [],
          totalDueForPeriod: 0,
          ownerName: '',
        };

        // Iterate over each taxable activity for the license
        const providers = calculatedLicenseTaxData.misc?.taxableActivities.map(
          (activity): TaxableActivity => {
            const provider = {
              key: activity.provider || 'All',
              sales: activity.totalRevenue,
              deductions: activity.allowableDeductions,
              taxableReceipts: activity.taxableReceipts,
              taxTypes: activity.taxTypes,
            };

            const totalDueForLicenseForPeriodForProvider = Object.values(
              activity.taxTypes
            ).reduce((sum, tax) => sum + tax.total, 0);
            license.totalDueForPeriod =
              (license.totalDueForPeriod || 0) +
              totalDueForLicenseForPeriodForProvider;
            taxablePeriod.totalDueForPeriod =
              (taxablePeriod.totalDueForPeriod || 0) +
              totalDueForLicenseForPeriodForProvider;

            return provider;
          }
        );
        license.providers = providers;
        taxablePeriod.licenses.push(license);
        return allTaxablePeriods; // Ensure to return the accumulator of reduce function
      },
      []
    );

    return displayTaxablePeriods; // Return the final grouped and processed data
  }, [calculatedTaxData, getAddress, findOrCreateGroupForPeriod]);

  const filteredData: CalculatedLicense[] = useMemo((): CalculatedLicense[] => {
    const [selectedPeriodFilter, selectedLicenseFilter] = selectedFilter;
    let filtered = convertedDataGroupedByPeriod;

    // period
    if (selectedPeriodFilter) {
      const [startDate, endDate] = selectedPeriodFilter.split('_');
      filtered = filtered.filter(
        (period) => period.startDate === startDate && period.endDate === endDate
      );
    }

    // license
    if (selectedLicenseFilter) {
      filtered = filtered
        .map((period) => ({
          ...period,
          licenses: period.licenses.filter(
            (license) => license.licenseNumber === selectedLicenseFilter
          ),
        }))
        .filter((period) => period.licenses.length > 0);
    }

    return filtered;
  }, [convertedDataGroupedByPeriod, selectedFilter]);

  // calculate total tax payable for the selected properties
  const totalPaymentDueRaw = useMemo(
    () =>
      filteredData.reduce(
        (total, reportingPeriod) =>
          total + (reportingPeriod.totalDueForPeriod ?? 0),
        0
      ),
    [filteredData]
  );
  const totalPaymentDue = useMemo(
    () => convertNumberToCurrency(totalPaymentDueRaw),
    [totalPaymentDueRaw]
  );

  const handleFinaliseOrNextClick = useCallback(() => {
    if (form.current?.check()) {
      dispatch(setSignatoryName(formValues.signatory));
      const totalPaymentDue = calculatedTaxData?.summary?.total;
      const paymentRequired = totalPaymentDue != null && totalPaymentDue > 0;
      if (paymentRequired) {
        dispatch(goToNextStep());
        placeNavigate('/' + selectedNextStepPath);
      } else {
        setProcessingPayNothing(true);
        dispatch(payNothingTax(selectedEmailAddress));
      }
    }
  }, [
    form.current,
    selectedEmailAddress,
    selectedNextStepPath,
    dispatch,
    formValues,
    calculatedTaxData,
    placeNavigate,
  ]);

  const handleDownloadSummaryClick = useCallback(async () => {
    const document = await downloadTaxSummary(
      calculatedTaxData?.paymentId as string,
      token
    );
    downloadBlob(document, 'tax-summary.xlsx');
  }, [calculatedTaxData?.paymentId, token]);

  return (
    <FlowLayout
      flow={FLOWS_ENUM.PAY_TOT}
      onNext={handleFinaliseOrNextClick}
      omitNavigationForward
      showNext
      title="Confirm and Sign"
    >
      <Text className="text-large mb-24">
        Please confirm the following information and sign below
      </Text>
      <Text
        as="h2"
        className="header-text-h4 text-bold mb-16"
      >
        Tax Summary
      </Text>
      <FlexboxGrid
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '16px',
          alignItems: 'center',
        }}
      >
        <TaxFilters
          data={convertedDataGroupedByPeriod}
          selectedValue={selectedFilter}
          onSelectedValue={setSelectedFilter}
        />
        {downloadSummary.enable && (
          <Button
            onClick={handleDownloadSummaryClick}
            className="btn-secondary"
            style={{ height: '36px' }}
          >
            <FileDownloadIcon style={{ marginRight: '5px' }} />
            {downloadSummary.label || 'Download Tax Summary'}
          </Button>
        )}
      </FlexboxGrid>
      <Divider className="bg-light-grey" />
      {filteredData?.map((period: CalculatedLicense) => (
        <TaxConfirmPeriodSection
          key={`period_${period.startDate}_${period.endDate}`}
          period={period}
        />
      ))}
      <Stack justifyContent="space-between">
        <Text
          className="text-bold"
          style={{ fontSize: '16px' }}
        >
          Total Payment Due
        </Text>
        <Text
          className="text-bold"
          style={{ fontSize: '16px', marginRight: '45px' }}
        >
          {totalPaymentDue}
        </Text>
      </Stack>
      <Divider className="bg-bright-zenith" />
      <Form
        ref={form}
        fluid
        checkTrigger="none"
        onCheck={(errors: Record<string, boolean>) => {
          setFormError(errors);
        }}
        formDefaultValue={formModel}
        onChange={(values) => {
          setFormValues(values as TaxConfirmFormValues);
        }}
        className="mt-24 pt-24"
      >
        <Text
          as="h2"
          id="tax-certifications-heading"
          className="header-text-h4 text-bold mb-12"
        >
          Certifications
        </Text>
        <fieldset
          aria-labelledby="tax-certifications-heading"
          style={{ border: 'none', margin: 0, padding: 0 }}
        >
          {certifications?.map((certificaton, idx) => {
            return (
              <AnnotatedCheckbox
                style={{ marginTop: 0, marginBottom: '16px' }}
                key={`certification_${idx}`}
                label={constructMarkup(certificaton)}
                hasError={formError[`certification_${idx}`]}
                errorMessage="You must agree to the terms to continue"
                name={`certification_${idx}`}
                validationRule={
                  validationModel[`certification_${idx}`] as CheckType<
                    boolean,
                    string
                  >
                }
              />
            );
          })}
        </fieldset>
        <Grid
          fluid
          className="pt-24"
        >
          <FlexboxGrid
            as={Row}
            align="bottom"
          >
            <Col
              xs={24}
              md={10}
              className="mb-16"
            >
              <FormTextField
                label="Legal Name of Authorized Signatory"
                name="signatory"
                required
                hasError={formError['signatory']}
                validationRule={validationModel.signatory}
                errorMessage="Enter the signatory name to continue"
                value={formValues?.signatory}
                autoComplete="name"
              />
            </Col>
            <Col
              xs={24}
              md={5}
              className="mb-16"
            >
              <Text style={{ marginBottom: '4px' }}>Date</Text>
              <Text className="report-revenue-calculated-value">
                {formValues?.date}
              </Text>
            </Col>
          </FlexboxGrid>
          {paymentCheckFailed && (
            <Row>
              <Col xs={24}>
                <Notice message="Something went wrong while processing your request." />
              </Col>
            </Row>
          )}
          {processingPayNothing && (
            <Row>
              <Col xs={24}>
                <IconIndicator />
              </Col>
            </Row>
          )}
        </Grid>
      </Form>
    </FlowLayout>
  );
};

export default TaxConfirmAndSign;
